import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useState } from "react";

import "./MemberAvatar.css";

export const MEMBER_AVATAR_UPDATED_EVENT = "eternua:member-avatar-updated";
const MEMBER_AVATAR_STORAGE_KEY = "eternua.member-avatar.updated.v1";
const REPORTED_ERRORS = new Set();

const SIZE_PIXELS = {
  xs: 24,
  sm: 36,
  md: 48,
  lg: 72,
  xl: 112,
  hero: 160,
};

const VARIANT_ORDER = {
  xs: ["sm", "md", "lg"],
  sm: ["sm", "md", "lg"],
  md: ["md", "sm", "lg"],
  lg: ["md", "lg", "sm"],
  xl: ["lg", "md", "sm"],
  hero: ["lg", "md", "sm"],
};

const cleanSource = (value) => typeof value === "string" ? value.trim() : "";

export function memberAvatarSources(member, { src = "", size = "md" } = {}) {
  const profiles = [member?.public_profile, member?.profile, member?.profil, member]
    .filter((value) => value && typeof value === "object");
  const order = VARIANT_ORDER[size] || VARIANT_ORDER.md;
  const aliases = { sm: "small", md: "medium", lg: "large" };
  const variants = (value) => order.flatMap((key) => [value?.[key], value?.[aliases[key]]]);
  // Keep server-provided display URLs ahead of legacy photo fields.
  return [...new Set([
    src,
    ...profiles.flatMap((profile) => [
      ...variants(profile.avatar_display_variants), profile.avatar_display_url,
    ]),
    ...profiles.flatMap((profile) => [
      ...variants(profile.avatar_variants), profile.avatar_url, profile.avatar,
    ]),
  ].map(cleanSource).filter(Boolean))];
}

export function memberAvatarSource(member, options) {
  return memberAvatarSources(member, options)[0] || "";
}

export function memberAvatarName(member, explicitName = "") {
  return String(
    explicitName ||
      member?.display_name ||
      member?.public_profile?.display_name ||
      member?.profile?.display_name ||
      member?.pseudo ||
      member?.username ||
      "Membre Eternua"
  ).trim();
}

export function memberAvatarInitials(member, explicitName = "") {
  const name = memberAvatarName(member, explicitName);
  return (
    (name.match(/[\p{L}\p{N}]+/gu) || [])
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase() || "E"
  );
}

function identityKey(member, explicitUsername = "") {
  return String(
    explicitUsername ||
      member?.username ||
      member?.public_profile?.username ||
      member?.profile?.username ||
      member?.id ||
      "member"
  ).trim();
}

function avatarPalette(key) {
  let hash = 0;
  for (const character of String(key || "member")) {
    hash = (hash * 31 + character.codePointAt(0)) >>> 0;
  }
  const first = hash % 360;
  const second = (first + 52 + (hash % 47)) % 360;
  // The lightest possible hue remains dark enough for white initials (WCAG AA).
  return `linear-gradient(145deg, hsl(${first} 72% 24%), hsl(${second} 78% 14%))`;
}

function normalizeUpdate(detail) {
  if (!detail || typeof detail !== "object") return null;
  const username = String(detail.username || "").trim();
  if (!username) return null;
  return {
    username,
    avatar_url: String(detail.avatar_display_url || detail.avatar_url || "").trim(),
    avatar_variants: detail.avatar_display_variants || detail.avatar_variants || {},
    removed: Boolean(detail.removed),
    nonce: String(detail.nonce || `${Date.now()}-${Math.random()}`),
  };
}

export function publishMemberAvatarUpdate(detail) {
  if (typeof window === "undefined") return;
  const payload = normalizeUpdate(detail);
  if (!payload) return;
  window.dispatchEvent(new CustomEvent(MEMBER_AVATAR_UPDATED_EVENT, { detail: payload }));
  try {
    window.localStorage.setItem(MEMBER_AVATAR_STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // L’événement de l’onglet courant reste suffisant si le stockage est indisponible.
  }
}

function reportAvatarError(src, username, context) {
  const url = String(src || "");
  if (!url.startsWith("/api/auth/public-profile/")) return;
  const reportableUrl = url.split("?", 1)[0];
  const key = `${username}:${url}`;
  if (REPORTED_ERRORS.has(key)) return;
  REPORTED_ERRORS.add(key);
  fetch("/api/auth/avatar-display-errors/", {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ avatar_url: reportableUrl, username, context }),
  }).catch(() => {});
}

export default function MemberAvatar({ "data-et-copy": __etCopy = "",
  member,
  src = "",
  name = "",
  username = "",
  size = "md",
  className = "",
  alt,
  decorative = false,
  eager = false,
  status,
  fallbackStyle = "initials",
  context = "member_avatar",
  onLoad,
  onError,
  style,
  ...imageProps
}) {
  useSourceLanguage();
  const canonicalUsername = identityKey(member, username);
  const label = memberAvatarName(member, name);
  const resolvedStatus =
    status ||
    (member?.deleted || member?.is_deleted || member?.account_status === "deleted"
      ? "deleted"
      : member?.blocked || member?.is_blocked || member?.relationship_status === "blocked"
        ? "blocked"
        : member?.private || member?.visibility === "private" || member?.can_view === false
          ? "private"
          : "active");
  const sourceSignature = JSON.stringify(memberAvatarSources(member, { src, size }));
  const [avatarUpdate, setAvatarUpdate] = useState(null);
  const sources = avatarUpdate?.owner === canonicalUsername && avatarUpdate.baseline === sourceSignature
    ? avatarUpdate.sources : JSON.parse(sourceSignature);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const applyUpdate = (payload) => {
      const update = normalizeUpdate(payload);
      if (!update || update.username.toLowerCase() !== canonicalUsername.toLowerCase()) return;
      const nextSources = update.removed
        ? []
        : memberAvatarSources(
            {
              username: update.username,
              avatar_url: update.avatar_url,
              avatar_variants: update.avatar_variants,
            },
            { size }
          );
      setAvatarUpdate({ owner: canonicalUsername, baseline: sourceSignature, sources: nextSources, nonce: update.nonce });
    };
    const onAvatarUpdate = (event) => applyUpdate(event.detail);
    const onStorage = (event) => {
      if (event.key !== MEMBER_AVATAR_STORAGE_KEY || !event.newValue) return;
      try {
        applyUpdate(JSON.parse(event.newValue));
      } catch (_) {
        // Une valeur de stockage invalide est ignorée.
      }
    };
    window.addEventListener(MEMBER_AVATAR_UPDATED_EVENT, onAvatarUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(MEMBER_AVATAR_UPDATED_EVENT, onAvatarUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, [canonicalUsername, size, sourceSignature]);

  const pixelSize = typeof size === "number" ? size : SIZE_PIXELS[size];
  const dimensionStyle =
    size === "fill"
      ? { width: "100%", height: "100%", minWidth: 0, ...style }
      : pixelSize
        ? { width: pixelSize, height: pixelSize, minWidth: pixelSize, fontSize: Math.max(10, Math.round(pixelSize * 0.375)), ...style }
        : style;
  const fallback = useMemo(() => {
    if (resolvedStatus === "blocked") return "×";
    if (resolvedStatus === "deleted") return "•";
    if (resolvedStatus === "private") return "◇";
    return memberAvatarInitials(member, name);
  }, [member, name, resolvedStatus]);
  const accessibleLabel =
    resolvedStatus === "blocked"
      ? "Membre bloqué"
      : resolvedStatus === "deleted"
        ? "Compte supprimé"
        : resolvedStatus === "private"
          ? `Photo privée de ${label}`
          : `Photo de profil de ${label}`;
  const illustratedFallback = fallbackStyle === "portrait" && resolvedStatus === "active";
  const rootClass = `member-avatar ${className}`.trim();


  const fallbackElement = (
    <span
      className={`${rootClass} member-avatar--fallback member-avatar--${resolvedStatus}`}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : alt ?? (illustratedFallback ? `Portrait de remplacement de ${label}` : accessibleLabel)}
      aria-hidden={decorative ? "true" : undefined}
      style={{
        background: illustratedFallback ? "linear-gradient(145deg, #293e51, #101c2a)" : avatarPalette(canonicalUsername || label),
        ...dimensionStyle,
      }}
    >
      {illustratedFallback ? <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
        <circle cx="32" cy="24" r="11" fill="#ddc7a5" />
        <path d="M10 64v-8c0-13 9-21 22-21s22 8 22 21v8" fill="#9aafbc" />
        <path d="M22 37l10 10 10-10" fill="none" stroke="#e5d3b6" strokeWidth="2" />
      </svg> : fallback}
    </span>
  );
  const canShowPhoto = sources.length > 0 && resolvedStatus !== "blocked" &&
    resolvedStatus !== "deleted" && member?.can_view !== false;
  if (!canShowPhoto) return fallbackElement;
  // Inline SVG keeps initials visible even while the network request is pending.
  const safeText = fallback.replace(/[<>&"']/g, "");
  const placeholder = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#253653"/><text x="32" y="33" dy=".35em" text-anchor="middle" font-family="Arial,sans-serif" font-size="24" font-weight="700" fill="white">${safeText}</text></svg>`)}`;
  return <MemberAvatarImage
    key={JSON.stringify([canonicalUsername, sources, avatarUpdate?.nonce])}
    sources={sources} fallbackElement={fallbackElement}
    rootClass={rootClass} placeholder={placeholder} dimensionStyle={dimensionStyle}
    pixelSize={pixelSize} imageProps={imageProps} decorative={decorative}
    alt={alt ?? accessibleLabel} eager={eager} onLoad={onLoad} onError={onError}
    username={canonicalUsername} context={context}
  />;
}

function MemberAvatarImage({ "data-et-copy": __etCopy = "", sources, fallbackElement, rootClass, placeholder,
  dimensionStyle, pixelSize, imageProps, decorative, alt, eager, onLoad, onError,
  username, context }) {
  useSourceLanguage();
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const source = sources[index];
  if (!source) return fallbackElement;
  return <img
    {...imageProps}
    key={source}
    className={`${rootClass}${loaded ? " member-avatar--loaded" : " member-avatar--loading"}`}
    src={source}
    alt={decorative ? "" : alt}
    aria-hidden={decorative ? "true" : undefined}
    loading={eager ? "eager" : "lazy"} decoding="async"
    width={pixelSize || imageProps.width} height={pixelSize || imageProps.height}
    style={{ ...dimensionStyle, ...(!loaded ? { backgroundImage: `url("${placeholder}")`, backgroundSize: "100% 100%" } : {}) }}
    aria-busy={!loaded}
    onLoad={(event) => { setLoaded(true); onLoad?.(event); }}
    onError={(event) => {
      setLoaded(false);
      setIndex((previous) => previous + 1);
      reportAvatarError(source, username, context);
      onError?.(event);
    }}
  />;
}
